// Pure helpers behind the Bible search box and the verse-library form.
// Moved verbatim out of ChurchApp.jsx (the logic is unchanged) so it can be
// tested without rendering the whole app.
import { bibleBooks, parseBibleReference } from './bibleReference';

/**
 * Tidies a reference as it is typed: "John 3 " -> "John 3:", "John 3 16" ->
 * "John 3:16", and "John 316" -> "John 3:16" (digits that cannot be a single
 * chapter are split into chapter and verse). A number that IS a valid chapter
 * is left alone, so "Psalm 23" and "John 21" stay as typed. Anything else is
 * returned as typed.
 */
export const autoFormatReferenceInput = (value) => {
  // Auto-insert colon after chapter number
  let processedValue = value;

  // Check if we have a book name followed by a chapter number without a colon
  const query = value.toLowerCase().trim();

  // Find if user typed a book name
  let foundBook = null;
  let bookKey = null;

  for (const key in bibleBooks) {
    const book = bibleBooks[key];
    const allNames = [key, book.name.toLowerCase(), ...book.abbr];

    for (let name of allNames) {
      if (query.startsWith(name.toLowerCase() + ' ')) {
        foundBook = book;
        bookKey = name;
        break;
      }
    }
    if (foundBook) break;
  }

  if (foundBook) {
    // Get what's after the book name (only the leading space is dropped: a
    // trailing space after the chapter is what triggers the colon below)
    const afterBook = value.substring(bookKey.length).replace(/^\s+/, '');

    // Check if it's just a chapter number (digits only, no colon yet)
    // Pattern: ends with number and space (user is about to type verse)
    const chapterMatch = afterBook.match(/^(\d+)\s+$/);
    if (chapterMatch) {
      // User typed "John 3 " - convert to "John 3:"
      processedValue = `${foundBook.name} ${chapterMatch[1]}:`;
    }

    // Handle when user types digits without space or colon. If the number is a
    // valid chapter for this book ("Psalm 23", "John 21") leave it alone;
    // otherwise split it into the longest valid chapter plus a verse
    // ("John 316" → "John 3:16", "Genesis 1050" → "Genesis 10:50").
    const directDigitsMatch = afterBook.match(/^(\d{2,})$/);
    if (directDigitsMatch && parseInt(directDigitsMatch[1], 10) > foundBook.chapters) {
      const digits = directDigitsMatch[1];
      for (let cut = digits.length - 1; cut >= 1; cut--) {
        const chapter = digits.slice(0, cut);
        const verse = digits.slice(cut);
        if (chapter[0] !== '0' && verse[0] !== '0' && parseInt(chapter, 10) <= foundBook.chapters) {
          processedValue = `${foundBook.name} ${chapter}:${verse}`;
          break;
        }
      }
    }

    // Also handle when user types a digit after "Book Chapter " without colon
    const chapterVerseMatch = afterBook.match(/^(\d+)\s+(\d+)$/);
    if (chapterVerseMatch && !afterBook.includes(':')) {
      // User typed "John 3 16" - convert to "John 3:16"
      processedValue = `${foundBook.name} ${chapterVerseMatch[1]}:${chapterVerseMatch[2]}`;
    }
  }

  return processedValue;
};

/**
 * Suggestions for the main Bible search box: the parsed reference, chapters
 * and verses for a recognised book, or matching book names. At most 10.
 * Each item is { type: 'reference' | 'chapter' | 'verse' | 'book', display, value?, parsed? }.
 */
export const buildSearchSuggestions = (text) => {
  const query = text.toLowerCase().trim();
  const suggestions = [];
  const seen = new Set();
  const push = (s) => {
    if (!seen.has(s.display)) { seen.add(s.display); suggestions.push(s); }
  };

  if (query.length < 1) {
    return [];
  }

  // 1. If the whole query already parses to a reference, offer it directly
  //    (works for both "John 3:16" verse refs and "John 3" whole-chapter refs).
  const parsed = parseBibleReference(text);
  if (parsed) {
    push({ type: 'reference', display: parsed.reference, parsed });
  }

  // 2. Find the book the user has started typing. Match the leading token
  //    against the full name and every abbreviation, longest match first so
  //    e.g. "1 co" resolves to 1 Corinthians rather than being missed.
  let foundBook = null;
  let bookKey = null;
  let bestLen = 0;
  for (const key in bibleBooks) {
    const book = bibleBooks[key];
    const allNames = [key, book.name.toLowerCase(), ...book.abbr];
    for (const name of allNames) {
      const n = name.toLowerCase();
      if (query.startsWith(n) && n.length > bestLen) {
        foundBook = book; bookKey = n; bestLen = n.length;
      }
    }
  }

  if (foundBook) {
    // 3. Book is known — suggest chapters or verses based on numbers typed.
    const afterBook = query.substring(bookKey.length).trim();
    const numbers = afterBook.match(/\d+/g);

    if (!numbers || numbers.length === 0) {
      for (let ch = 1; ch <= Math.min(foundBook.chapters, 12); ch++) {
        push({ type: 'chapter', display: `${foundBook.name} ${ch}`, value: `${foundBook.name} ${ch}` });
      }
    } else if (numbers.length <= 2) {
      const chapter = Math.min(parseInt(numbers[0]), foundBook.chapters);
      // If the user has already typed a colon and a verse ("John 3:5"), suggest
      // sequential verses from there; otherwise ("John 3" or "John 3:") offer
      // the whole chapter plus the first several verses in order.
      const verseTyped = numbers.length === 2 ? afterBook.match(/:\s*(\d+)/) : null;
      if (verseTyped) {
        const start = parseInt(verseTyped[1]);
        for (let v = start; v < start + 8; v++) {
          push({ type: 'verse', display: `${foundBook.name} ${chapter}:${v}`, value: `${foundBook.name} ${chapter}:${v}` });
        }
      } else if (numbers.length === 1) {
        push({ type: 'reference', display: `${foundBook.name} ${chapter}`,
          parsed: parseBibleReference(`${foundBook.name} ${chapter}`) });
        for (let v = 1; v <= 9; v++) {
          push({ type: 'verse', display: `${foundBook.name} ${chapter}:${v}`, value: `${foundBook.name} ${chapter}:${v}` });
        }
      }
    }
  } else {
    // 4. No leading-token book match — do a friendlier "contains" search over
    //    book names AND abbreviations so partial/mid-word queries still help
    //    (e.g. "corin" → 1 & 2 Corinthians, "rev" → Revelation).
    const prefix = [];
    const contains = [];
    for (const key in bibleBooks) {
      const book = bibleBooks[key];
      const nm = book.name.toLowerCase();
      const abbrHit = book.abbr.some(a => a.toLowerCase().startsWith(query));
      if (nm.startsWith(query) || key.startsWith(query) || abbrHit) {
        prefix.push(book.name);
      } else if (nm.includes(query) || key.includes(query)) {
        contains.push(book.name);
      }
    }
    [...prefix, ...contains].forEach(name =>
      push({ type: 'book', display: name, value: `${name} ` }));
  }

  return suggestions.slice(0, 10);
};

/**
 * Suggestions for the "add to verse library" input. Needs at least 2
 * characters. At most 8.
 */
export const buildLibrarySuggestions = (text) => {
  const query = text.toLowerCase().trim();
  const suggestions = [];

  if (query.length < 2) {
    return [];
  }

  const parsed = parseBibleReference(text);

  if (parsed && parsed.verseStart) {
    suggestions.push({ type: 'reference', display: parsed.reference, parsed });
  }

  let foundBook = null;
  let bookKey = null;

  for (const key in bibleBooks) {
    const book = bibleBooks[key];
    const allNames = [key, book.name.toLowerCase(), ...book.abbr];

    for (let name of allNames) {
      if (query.startsWith(name.toLowerCase())) {
        foundBook = book;
        bookKey = name;
        break;
      }
    }

    if (!foundBook && book.name.toLowerCase().startsWith(query)) {
      suggestions.push({ type: 'book', display: book.name, value: book.name });
    }

    if (foundBook) break;
  }

  if (foundBook) {
    const afterBook = query.substring(bookKey.length).trim();
    const numbers = afterBook.match(/\d+/g);

    if (!numbers || numbers.length === 0) {
      for (let ch = 1; ch <= Math.min(foundBook.chapters, 8); ch++) {
        suggestions.push({
          type: 'chapter',
          display: `${foundBook.name} ${ch}:`,
          value: `${foundBook.name} ${ch}:`
        });
      }
    } else if (numbers.length === 1) {
      const chapter = parseInt(numbers[0]);

      for (let v of [1, 2, 3, 5, 10, 15, 16, 20]) {
        suggestions.push({
          type: 'verse',
          display: `${foundBook.name} ${chapter}:${v}`,
          value: `${foundBook.name} ${chapter}:${v}`
        });
      }
    }
  }

  return suggestions.slice(0, 8);
};
